import { getLocaleCurrencyStringFromNumber } from 'utils'

import { type MarketplacePriceCadence } from './types'

/**
 * Calculates the discount display string based on base and discounted prices.
 * It compares the floored integer value of the percentage discount with the amount discount,
 * returning the larger value formatted appropriately (e.g. "15% off" or "$5 off").
 *
 * If the floored integer value of the percentage discount is less than 1,
 * it returns the amount discount.
 *
 * @param baseCents the original price in cents
 * @param discountedCents the discounted price in cents
 * @returns a string representing the discount
 */
export const calculateDiscountDisplay = (baseCents: number, discountedCents: number, productSlug: string): string => {
  if (baseCents <= discountedCents || baseCents === 0) {
    return ''
  }

  const amountDiff = (baseCents - discountedCents) / 100
  const percentDiff = ((baseCents - discountedCents) / baseCents) * 100

  const flooredPercentOff = Math.floor(percentDiff)

  // TODO(yousef): move this to model?
  const discountDescription = productSlug === 'function-health-membership' ? 'credit' : 'off'

  if (flooredPercentOff >= 1 && flooredPercentOff > amountDiff) {
    return `${flooredPercentOff}% ${discountDescription}`
  } else if (amountDiff >= 1) {
    return `${getLocaleCurrencyStringFromNumber(Math.floor(amountDiff), { maximumFractionDigits: 0 })} ${discountDescription}`
  } else {
    return `${getLocaleCurrencyStringFromNumber(amountDiff, { maximumFractionDigits: 2 })} ${discountDescription}`
  }
}

export const getPriceCadenceSuffix = (cadence: MarketplacePriceCadence): string => {
  switch (cadence) {
    case 'monthly': return '/mo'
    case 'yearly': return '/yr'
    case 'per_session': return '/session'
    case 'one_time': return ''
  }
}

export const getDiscountTextSuffix = (cadence: MarketplacePriceCadence): string => {
  switch (cadence) {
    case 'monthly': return '1st month'
    case 'yearly': return '1st year'
    case 'per_session': return '1st session'
    case 'one_time': return ''
  }
}

export const isProductNew = (createdAtString: string): boolean => {
  const createdAt = new Date(createdAtString)
  const sixWeeksAgo = new Date()
  sixWeeksAgo.setDate(sixWeeksAgo.getDate() - (6 * 7)) // 6 weeks or 42 days

  return createdAt > sixWeeksAgo
}

export const getProductBadges = ({
  createdAt,
  hasDiscount,
  comingSoon,
}: {
  createdAt: string
  hasDiscount: boolean
  comingSoon: boolean
}): string[] | undefined => {
  const badges = []

  if (comingSoon) {
    badges.push('Coming soon')
  } else if (isProductNew(createdAt)) {
    badges.push('New')
  }

  if (hasDiscount) {
    badges.push('Thatch exclusive')
  }

  return badges.length > 0 ? badges : undefined
}
